// TipModal — 文章打赏弹窗(lNIg8)
//
// frame_id=lNIg8 @ ui/dogdisk-user.pen
// 触发:文章详情页打赏按钮 → 底部弹层
//
// 依赖:
//   - useTipArticle  (frontend/user/src/_lib/tip-api.ts)
//   - useCoinBalance (frontend/user/src/_lib/wallet-api.ts) — data.balance: string
//   - useCurrentUser (@dogdisk/shared) — user.user_id
//   - ApiClientError (@dogdisk/shared) — err.code === 'INSUFFICIENT_BALANCE'
//
// data-testid 约定:
//   - 根:tip-modal
//   - chip:tip-chip-{n}  (6/30/66/188/520/1314)
//   - 自定义输入:tip-custom
//   - 余额行:tip-balance
//   - 充值:tip-recharge
//   - 确认:tip-confirm
//   - 错误:tip-error

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiClientError, useCurrentUser } from '@dogdisk/shared'
import { useTipArticle } from '../../_lib/tip-api'
import { useCoinBalance } from '../../_lib/wallet-api'

const CHIPS = [6, 30, 66, 188, 520, 1314] as const
const DEFAULT_CHIP = 66

export type TipModalProps = {
  open: boolean
  articleId: string
  authorName: string
  onClose: () => void
  onSuccess?: (() => void) | undefined
}

export function TipModal({ open, articleId, authorName, onClose, onSuccess }: TipModalProps) {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const { data: walletData } = useCoinBalance(user?.user_id)
  const tipMut = useTipArticle(articleId)

  const [selectedChip, setSelectedChip] = useState<number>(DEFAULT_CHIP)
  const [customValue, setCustomValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isInsufficient, setIsInsufficient] = useState(false)

  if (!open) return null

  const parsedCustom = customValue !== '' ? parseInt(customValue, 10) : NaN
  const amount = !isNaN(parsedCustom) && customValue !== '' ? parsedCustom : selectedChip
  const isDisabled = amount < 1 || tipMut.isPending

  const balanceRaw = walletData?.balance
  const balanceDisplay =
    balanceRaw == null ? '—' : Number(balanceRaw).toLocaleString()

  function handleChipClick(n: number) {
    setSelectedChip(n)
    setCustomValue('')
    setError(null)
    setIsInsufficient(false)
  }

  function handleCustomChange(e: React.ChangeEvent<HTMLInputElement>) {
    // only digits
    const digits = e.target.value.replace(/[^0-9]/g, '')
    setCustomValue(digits)
    if (digits !== '') {
      setSelectedChip(0) // deselect all chips
    } else {
      setSelectedChip(DEFAULT_CHIP) // cleared → restore default chip
    }
    setError(null)
    setIsInsufficient(false)
  }

  async function handleConfirm() {
    setError(null)
    setIsInsufficient(false)
    try {
      await tipMut.mutateAsync(amount)
      onSuccess?.()
      onClose()
    } catch (e) {
      if (e instanceof ApiClientError && e.code === 'INSUFFICIENT_BALANCE') {
        setIsInsufficient(true)
        setError('金币余额不足,请先充值')
      } else {
        setError('打赏失败,请稍后再试')
      }
    }
  }

  return (
    <div
      data-frame-id="lNIg8"
      data-testid="tip-modal"
      role="dialog"
      aria-modal="true"
      aria-label="打赏作者"
      className="fixed inset-0 z-50 flex items-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="mx-auto w-full max-w-[402px] rounded-t-[24px] bg-white"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle bar */}
        <div className="flex h-6 items-center justify-center pt-2">
          <span className="h-1 w-10 rounded-full bg-[#E5E7EB]" />
        </div>

        {/* Header: title + close button */}
        <div className="flex items-center justify-between px-5 pb-3 pt-1">
          <h2 className="font-['Bricolage_Grotesque',sans-serif] text-[20px] font-bold text-[#1A1A1A]">
            打赏作者
          </h2>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F6F7F8]"
          >
            {/* X icon */}
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round">
              <line x1="2" y1="2" x2="12" y2="12" />
              <line x1="12" y1="2" x2="2" y2="12" />
            </svg>
          </button>
        </div>

        {/* Author row */}
        <div className="mx-5 mb-4 flex items-center gap-3 rounded-xl bg-[#FFF7ED] px-3 py-2.5">
          {/* user-round icon */}
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
          </span>
          <div className="min-w-0">
            <p className="truncate text-[15px] font-semibold text-[#1A1A1A]">{authorName}</p>
            <p className="text-[12px] text-[#9CA3AF]">为TA的创作加油 ✨</p>
          </div>
        </div>

        {/* Chip label */}
        <p className="px-5 pb-2 text-[13px] font-medium text-[#9CA3AF]">选择金币数量</p>

        {/* Chips grid: 2 rows x 3 cols */}
        <div className="mx-5 mb-3 grid grid-cols-3 gap-2">
          {CHIPS.map((n) => {
            const isSelected = customValue === '' && selectedChip === n
            return (
              <button
                key={n}
                type="button"
                data-testid={`tip-chip-${n}`}
                onClick={() => handleChipClick(n)}
                className={[
                  'flex h-16 flex-col items-center justify-center rounded-xl text-[15px] font-semibold transition-colors',
                  isSelected
                    ? 'border-[1.5px] border-[#F59E0B] bg-[#FFF7ED] text-[#F59E0B]'
                    : 'border border-[#E5E7EB] bg-white text-[#1A1A1A]',
                ].join(' ')}
              >
                <span className="text-[18px] font-bold">{n}</span>
                <span className="text-[11px] font-normal text-[#9CA3AF]">金币</span>
              </button>
            )
          })}
        </div>

        {/* Custom input */}
        <div className="mx-5 mb-3 flex h-12 items-center gap-2 rounded-xl border border-[#E5E7EB] bg-white px-3">
          {/* circle-dollar-sign icon */}
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F5B300" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v12M9 9c0-1.66 1.34-3 3-3s3 1.34 3 3-1.34 3-3 3-3 1.34-3 3 1.34 3 3 3 3-1.34 3-3" />
          </svg>
          <input
            type="text"
            inputMode="numeric"
            data-testid="tip-custom"
            value={customValue}
            onChange={handleCustomChange}
            placeholder="自定义金币数量"
            className="min-w-0 flex-1 bg-transparent text-[14px] text-[#1A1A1A] outline-none placeholder:text-[#9CA3AF]"
          />
          <span className="shrink-0 text-[13px] text-[#9CA3AF]">金币</span>
        </div>

        {/* Balance row */}
        <div className="mx-5 mb-3 flex items-center gap-1.5">
          {/* wallet icon */}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="5" width="22" height="14" rx="2" />
            <path d="M1 10h22" />
            <circle cx="17" cy="15" r="1.5" fill="#9CA3AF" stroke="none" />
          </svg>
          <span className="text-[13px] text-[#9CA3AF]">金币余额</span>
          <span data-testid="tip-balance" className="text-[13px] font-semibold text-[#D97706]">
            {balanceDisplay}
          </span>
          <span className="flex-1" />
          <button
            type="button"
            data-testid="tip-recharge"
            onClick={() => navigate('/wallet/recharge')}
            className={[
              'text-[13px] font-medium text-[#19caad]',
              isInsufficient ? 'font-bold underline' : '',
            ].join(' ').trim()}
          >
            充值 &gt;
          </button>
        </div>

        {/* Error message */}
        {error && (
          <p data-testid="tip-error" className="mx-5 mb-2 text-[12px] text-[#FF6B6B]">
            {error}
          </p>
        )}

        {/* Confirm button */}
        <div className="px-5 pb-1">
          <button
            type="button"
            data-testid="tip-confirm"
            disabled={isDisabled}
            onClick={handleConfirm}
            className="flex h-[50px] w-full items-center justify-center gap-2 rounded-[25px] text-[16px] font-bold text-white disabled:opacity-50"
            style={{
              background: isDisabled ? '#F59E0B' : 'linear-gradient(90deg, #FBBF24, #F59E0B)',
              boxShadow: isDisabled ? 'none' : '0 4px 16px 0 #F59E0B55',
            }}
          >
            {/* gift icon */}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="9" width="20" height="13" rx="2" />
              <path d="M12 9v13" />
              <path d="M2 13h20" />
              <path d="M8 9a3 3 0 0 1 0-6 5 5 0 0 1 4 3 5 5 0 0 1 4-3 3 3 0 0 1 0 6" />
            </svg>
            确认打赏 · {amount} 金币
          </button>
        </div>
      </div>
    </div>
  )
}
